import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';

import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { useGlobalPageSize } from '../../../utils/tableUtils';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { actions } from './redux/actions';
import { isSuperUser, isOrganizationManager } from '../../../utils/userRoles';
import { DATE_TIME_SPACE_12_UPPERCASE, HEADER_BUTTON_BLUE } from '../../../constants';
import OrgSelect from '../../../components/OrgSelect/OrgSelect';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition, OrgGroupFilter } from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import { openModalAction } from '../../../actions/modal';
import userActions, { INVITE_USER } from '../../../actions/user';

function AdminUsersTab(props) {
  const globalPageSize = useGlobalPageSize();
  const { isLoading, users, pagination, orgIds, filters } = props;
  const { getUsers, onNavigate, getUserFormBundle, addUser } = props;

  // Determine if user can see org column (superusers and org managers can see it)
  const showOrgColumn = isSuperUser() || isOrganizationManager();

  // Filter state
  const [sortColumn, setSortColumn] = React.useState('created_at');
  const [sortType, setSortType] = React.useState('descending');
  const [orgGroups, setOrgGroups] = React.useState([]);

  const params = React.useRef({
    offset: 0,
    limit: globalPageSize,
    sortColumn,
    sortType,
    orgId: orgIds,
  });

  // Fetch org groups and organizations on mount
  React.useEffect(() => {
    if (showOrgColumn) {
      if (getUserFormBundle) {
        getUserFormBundle().then(resp => {
          if (resp?.response?.org_groups) {
            setOrgGroups(resp.response.org_groups?.map(g => ({ ...g, id: g.uuid })));
          }
        });
      }
    }
  }, [showOrgColumn, getUserFormBundle]);

  const onSearchChange = e => {
    const query = e.target.value;
    return onSearchQueryChangeDebounced(query);
  };

  const loadUsers = () => {
    getUsers(params.current);
  };

  const onSearchQueryChangeDebounced = _.debounce(query => {
    params.current.offset = 0;
    params.current.search = query;
    loadUsers();
  }, 1000);

  React.useEffect(() => {
    params.current.orgId = orgIds;
    params.current.offset = 0;
    loadUsers();
  }, [orgIds]);

  const getPageRequestFromFilters = (filterState) => {
    const ret = {};

    const archetypeFilter = getRelevantFilters(filterState.filter(f => f.group === 'archetype'));
    if (archetypeFilter?.length > 0) {
      ret.archetype = archetypeFilter.map(f => f.value).join(',');
    } else {
      ret.archetype = undefined;
    }

    const statusFilter = getRelevantFilters(filterState.filter(f => f.group === 'user_state'));
    if (statusFilter?.some(f => f.value === 'all')) {
      ret.is_active = undefined;
    } else if (statusFilter?.length > 0) {
      ret.is_active = statusFilter.map(f => Boolean(f.value === 'true')).join(',');
    } else {
      ret.is_active = undefined;
    }

    const orgGrpFilter = getRelevantFilters(filterState.filter(f => f.group === 'orgGroup'));
    if (orgGrpFilter?.length > 0) {
      ret.org_group = orgGrpFilter.map(f => f.value).join(',');
    } else {
      ret.org_group = undefined;
    }

    return ret;
  };

  React.useEffect(() => {
    let newFiltersForRequest = getPageRequestFromFilters(filters);
    if (newFiltersForRequest?.length === 0) {
      newFiltersForRequest = undefined;
    }
    if (newFiltersForRequest.archetype === params.current.filters?.archetype &&
        newFiltersForRequest.is_active === params.current.filters?.is_active &&
        newFiltersForRequest.org_group === params.current.filters?.org_group) {
      return;
    }
    params.current = { ...params.current, ...newFiltersForRequest };
    params.current.offset = 0;
    loadUsers();
  }, [filters]);
  
  const onOffsetChange = (offset, limit) => {
    if (offset === params.current.offset && limit === params.current.limit) {
      return;
    }
    params.current.offset = offset;
    if (limit) {
      params.current.limit = limit;
    }
    loadUsers();
  };

  const onPageSizeChange = newSize => {
    params.current.offset = 0;
    params.current.limit = newSize;
    loadUsers();
  };

  const onSortingChange = (sortingState) => {
    if (sortingState && sortingState.length > 0) {
      const columnId = sortingState[0].id;
      setSortColumn(columnId);
      setSortType(sortingState[0].desc ? 'descending' : 'ascending');
      params.current.sortColumn = columnId;
      params.current.sortType = sortingState[0].desc ? 'descending' : 'ascending';
      params.current.offset = 0;
      loadUsers();
    }
  };

  const handleRowClick = (rowData) => {
    if (onNavigate) {
      onNavigate(`/admin/users/${rowData.uuid || rowData.id}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = moment(dateString);
    return date.format(DATE_TIME_SPACE_12_UPPERCASE);
  };

  const columns = React.useMemo(() => {
    const cols = [
      columnHelper.accessor('name', {
        header: Strings.name,
        cell: info => {
          const row = info.row.original;
          return `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-';
        },
        enableSorting: false,
      }),
      columnHelper.accessor('email', {
        header: Strings.email,
      }),
      columnHelper.accessor('archetype', {
        header: Strings.archetype,
        enableSorting: false,
        cell: info => info.getValue() || '-',
      }),
    ];

    if (showOrgColumn) {
      cols.push(
        columnHelper.accessor('organization', {
          accessorFn: row => row.organization_name,
          header: Strings.organization,
          cell: info => info.getValue() || '-',
        })
      );
    }

    cols.push(
      columnHelper.accessor('original_role_title', {
        header: Strings.role,
        enableSorting: false,
        cell: info => info.getValue() || '-',
      }),
      columnHelper.accessor('created_at', {
        header: Strings.created || 'Created',
        enableSorting: false,
        cell: info => formatDate(info.getValue()),
      }),
      columnHelper.accessor('last_login', {
        header: Strings.lastLogin || 'Last Login',
        enableSorting: false,
        cell: info => formatDate(info.getValue()),
      }),
      columnHelper.accessor('is_active', {
        header: Strings.status,
        enableSorting: false,
        cell: info => (info.getValue() ? Strings.active || 'Active' : Strings.inactive || 'Inactive'),
      })
    );

    return cols;
  }, [showOrgColumn]);

  const getHeaderComponents = () => (
    <>
      <TextInput
        class="search"
        placeholder={Strings.search}
        onChange={onSearchChange}
      />
      <OrgSelect />
      <AdvancedFilters>
        <Filter definition={getFiltersDefinition().archetype} />
        <Filter definition={getFiltersDefinition().user_state} />
        {orgGroups?.length > 0 && <Filter definition={OrgGroupFilter(orgGroups).orgGroup} />}
      </AdvancedFilters>
      <Button
        class={HEADER_BUTTON_BLUE}
        onClick={() => addUser(loadUsers)}
        title={`${Strings.addUser || 'Add User'}`}
        testid={`add-user`}
      />
    </>
  );

  return (
    <>
      <PageHeader left={Strings.users} right={getHeaderComponents()} />
      <AdvancedFiltersBar customFilters={OrgGroupFilter(orgGroups)} />
      <Table3
        data={users || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        onRowClick={handleRowClick}
        noDataText={Strings.noUsersFound || 'No users found'}
      />
    </>
  );
}

const mapStateToProps = state => ({
  isLoading: state.adminUsers?.isLoading || false,
  users: state.adminUsers?.users || [],
  pagination: state.adminUsers?.pagination,
  orgIds: state.entities?.orgFilter?.orgIds,
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getUsers: params => dispatch(actions.getUsers(params)),
  addUser: onSuccess =>
    dispatch(
      openModalAction('edit-user', {
        action: userActions.invite,
        actionType: INVITE_USER,
        isGenericUser: true,
        onSuccess,
      }),
    ),
  getUserFormBundle: () => dispatch(actions.getUserFormBundle()),
  onNavigate: path => dispatch(push(path)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminUsersTab);
